import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { samsaraRouteCleanup, type CleanupRoute, type CleanupResult } from '../../services/samsaraRouteCleanup.service';

function message(error: unknown): string {
  const detail = isAxiosError(error) ? error.response?.data?.detail : undefined;
  return typeof detail === 'string' ? detail : error instanceof Error ? error.message : 'Request failed. You can retry.';
}

/** Temporary production cleanup; remove this panel and its endpoints after cutover. */
export default function SamsaraRouteCleanupPanel() {
  const [routes, setRoutes] = useState<CleanupRoute[]>([]);
  const [preserved, setPreserved] = useState(0);
  const [ready, setReady] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [busy, setBusy] = useState<'preview' | 'delete' | 'verify' | null>(null);
  const [processed, setProcessed] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [error, setError] = useState('');
  const [verification, setVerification] = useState('');
  const queue = useRef<string[]>([]);
  const outcomes = useRef(new Map<string, CleanupResult>());
  const stop = useRef(false);
  useEffect(() => () => { stop.current = true; }, []);

  async function inventory(onProgress?: (count: number) => void) {
    const found: CleanupRoute[] = [];
    const batches: string[] = [];
    const cursors = new Set<string>();
    const ids = new Set<string>();
    let after: string | null = null;
    let others = 0;
    do {
      if (stop.current) throw new Error('Stopped. Preview again to load a complete inventory.');
      const page = await samsaraRouteCleanup.preview(after);
      for (const route of page.routes) {
        if (ids.has(route.id)) throw new Error('Inventory changed during preview. Preview again before deleting.');
        ids.add(route.id);
        found.push(route);
      }
      batches.push(...page.batches);
      others += page.preserved;
      after = page.after;
      onProgress?.(found.length);
      if (after && cursors.has(after)) throw new Error('Incomplete inventory. Preview again.');
      if (after) cursors.add(after);
    } while (after);
    return { found, batches, others };
  }

  async function preview() {
    stop.current = false;
    setBusy('preview'); setError(''); setReady(false); setDownloaded(false); setVerification('');
    setProcessed(0); setRoutes([]); queue.current = []; outcomes.current.clear();
    try {
      const data = await inventory(setRemaining);
      setRoutes(data.found); setPreserved(data.others); queue.current = data.batches;
      setRemaining(data.found.length); setReady(true);
    } catch (e) { setError(message(e)); }
    finally { setBusy(null); }
  }

  function download() {
    const blob = new Blob([JSON.stringify({ capturedAt: new Date().toISOString(), routes }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url; anchor.download = `gwi360-samsara-routes-${Date.now()}.json`;
    anchor.click(); URL.revokeObjectURL(url); setDownloaded(true);
  }

  async function remove() {
    stop.current = false; setBusy('delete'); setError('');
    try {
      while (queue.current.length && !stop.current) {
        const result = await samsaraRouteCleanup.remove(queue.current[0]);
        for (const row of result.results) if (!outcomes.current.has(row.id)) outcomes.current.set(row.id, row);
        setProcessed(outcomes.current.size);
        setRemaining(routes.length - outcomes.current.size);
        if (result.error) throw new Error(result.error);
        queue.current.shift();
      }
      if (!queue.current.length && !stop.current) {
        setBusy('verify');
        const result = await inventory();
        setVerification(`${result.found.length.toLocaleString()} 360-created routes remain. ${result.others.toLocaleString()} other routes found.`);
      }
    } catch (e) { setError(message(e)); }
    finally { setBusy(null); }
  }

  // Hiding the card is convenience only; the server enforces production and global admin.
  if (import.meta.env.VITE_ENV !== 'production' || import.meta.env.DEV) return null;
  return <section className="bg-panel shadow rounded-lg p-6 mt-6">
    <h2 className="text-lg font-medium text-heading mb-3">Temporary Samsara route cleanup</h2>
    <p className="text-sm text-muted mb-3">Delete routes created by 360, including completed routes and their stops.
      Other routes, saved places, geofences, and Live Share links are preserved. Global administrators only.</p>
    <p className="text-sm text-muted mb-4">Keep Samsara Route ETA Sync disabled. Leave this page open while cleanup runs;
      closing it stops after the current batch. You can preview again to continue later.</p>
    <div className="flex flex-wrap gap-3">
      <button type="button" disabled={!!busy} onClick={preview} className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50">Preview 360 routes</button>
      {ready && routes.length > 0 && <>
        <button type="button" disabled={!!busy} onClick={download} className="px-4 py-2 rounded border border-gray-300 text-label disabled:opacity-50">Download route backup</button>
        <button type="button" disabled={!!busy || !downloaded || !queue.current.length} onClick={remove}
          className="px-4 py-2 rounded bg-red-600 text-white disabled:opacity-50">
          {processed ? 'Resume cleanup' : `Delete ${routes.length.toLocaleString()} 360-created routes`}
        </button>
      </>}
      {busy && <button type="button" onClick={() => { stop.current = true; }} className="px-4 py-2 rounded border border-gray-300 text-label">Stop after current batch</button>}
    </div>
    <div role="status" className="mt-3 text-sm text-label">
      {busy === 'preview' ? `Loading inventory… ${remaining.toLocaleString()} 360 routes found.` : ready && `${routes.length.toLocaleString()} 360-created routes selected; ${preserved.toLocaleString()} other routes excluded.`}
      {(processed > 0 || busy === 'delete') && <p>{processed.toLocaleString()} of {routes.length.toLocaleString()} processed; {remaining.toLocaleString()} remaining.</p>}
      {busy === 'verify' && <p>Verifying remaining routes…</p>}
      {verification && <p>{verification}</p>}
    </div>
    {ready && routes.length > 0 && !downloaded && <p className="mt-2 text-sm text-muted">Download the backup to enable deletion.</p>}
    {error && <p role="alert" className="mt-3 text-sm text-red-600">{error}</p>}
  </section>;
}
